import { ChangeDetectorRef, Component, ElementRef, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CINote } from './ci-note.model';
import { CINoteService } from './ci-note.service';
import { AuthService } from '../../shared/core/index';

@Component({
    selector: 'notes-panel',
    standalone: false,
    templateUrl: './notes-panel.component.html',
    styleUrls: ['./notes-panel.component.css']
})
export class NotesPanelComponent implements OnChanges {
    @Input() clearanceItemId: string;
    @Input() unreadNotesCount: number = 0;
    @Output() notesRead = new EventEmitter<void>();

    @ViewChild('notesBody') notesBody: ElementRef<HTMLElement>;
    @ViewChild('notesPanel') notesPanelEl: ElementRef<HTMLElement>;

    // Live-resizable dimensions (bound to the panel). null = use the CSS defaults.
    panelWidth: number = null;
    panelHeight: number = null;

    private readonly MIN_WIDTH = 320;
    private readonly MIN_HEIGHT = 360;
    private isResizing = false;
    private resizeStartX = 0;
    private resizeStartY = 0;
    private resizeStartWidth = 0;
    private resizeStartHeight = 0;
    private boundResizeMove: (e: MouseEvent) => void;
    private boundResizeEnd: () => void;

    isOpen: boolean = false;
    notes: CINote[] = [];
    newNoteText: string = '';
    selectedRole: string = 'Production';
    filterRole: string = null;
    filterUnread: boolean = false;
    editingNote: CINote = null;
    editText: string = '';
    isSending: boolean = false;

    // Ids of notes that were unread for the current user when the notes were loaded / panel opened.
    // We snapshot this so that opening the panel (which marks notes read on the server) does not
    // instantly hide the unread styling and make the "Unread" filter useless within the same session.
    private unreadSnapshot: Set<number> = new Set<number>();

    constructor(
        private _noteService: CINoteService,
        private _authService: AuthService,
        private _cdr: ChangeDetectorRef
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['clearanceItemId'] && this.clearanceItemId) {
            this.isOpen = false;
            this.loadNotes();
        }
    }

    // Resize from the TOP-LEFT handle. The panel is anchored bottom-right, so dragging up/left grows it.
    startResize(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        const el = this.notesPanelEl && this.notesPanelEl.nativeElement;
        if (!el) {
            return;
        }

        const rect = el.getBoundingClientRect();
        this.isResizing = true;
        this.resizeStartX = event.clientX;
        this.resizeStartY = event.clientY;
        this.resizeStartWidth = rect.width;
        this.resizeStartHeight = rect.height;

        this.boundResizeMove = (e: MouseEvent) => this.onResizeMove(e);
        this.boundResizeEnd = () => this.endResize();
        document.addEventListener('mousemove', this.boundResizeMove);
        document.addEventListener('mouseup', this.boundResizeEnd);
        document.body.style.userSelect = 'none';
    }

    private onResizeMove(event: MouseEvent): void {
        if (!this.isResizing) {
            return;
        }
        // Handle is at the top-left; moving left/up (negative delta) increases the size.
        const deltaX = this.resizeStartX - event.clientX;
        const deltaY = this.resizeStartY - event.clientY;

        const maxWidth = window.innerWidth - 48;
        const maxHeight = window.innerHeight - 120;

        this.panelWidth = Math.max(this.MIN_WIDTH, Math.min(maxWidth, this.resizeStartWidth + deltaX));
        this.panelHeight = Math.max(this.MIN_HEIGHT, Math.min(maxHeight, this.resizeStartHeight + deltaY));
        this._cdr.detectChanges();
    }

    private endResize(): void {
        this.isResizing = false;
        if (this.boundResizeMove) {
            document.removeEventListener('mousemove', this.boundResizeMove);
        }
        if (this.boundResizeEnd) {
            document.removeEventListener('mouseup', this.boundResizeEnd);
        }
        document.body.style.userSelect = '';
    }

    get isClearanceUser(): boolean {
        return this._authService.isUserInRole(environment.clearanceRole);
    }

    get filteredNotes(): CINote[] {
        let result = this.notes;
        if (this.filterRole) {
            result = result.filter(n => n.role === this.filterRole);
        }
        if (this.filterUnread) {
            result = result.filter(n => this.isUnread(n));
        }
        return result;
    }

    toggle(): void {
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
            this.scrollToBottom();
        }

        if (this.isOpen && this.clearanceItemId) {
            // Snapshot what is currently unread BEFORE marking read, so the "Unread" filter and unread
            // styling remain meaningful for this session even after the server marks the notes read.
            this.captureUnreadSnapshot();

            this._noteService.markAsRead(this.clearanceItemId).subscribe(() => {
                if (this.isClearanceUser) {
                    this.notes.forEach(n => {
                        if (n.role === 'Production') { n.isReadByClearance = true; }
                    });
                } else {
                    this.notes.forEach(n => {
                        if (n.role === 'Clearance') { n.isReadByProduction = true; }
                    });
                }
                this.unreadNotesCount = 0;
                this.notesRead.emit();
                this._cdr.detectChanges();
            });
        }

        this._cdr.detectChanges();
    }

    private captureUnreadSnapshot(): void {
        this.unreadSnapshot = new Set<number>(
            this.notes.filter(n => this.isServerUnread(n)).map(n => n.id)
        );
    }

    // Whether a note is unread according to the latest server flags (used to build the snapshot).
    private isServerUnread(note: CINote): boolean {
        if (this.isClearanceUser) {
            return note.role === 'Production' && !note.isReadByClearance;
        }
        return note.role === 'Clearance' && !note.isReadByProduction;
    }

    toggleFilter(role: string): void {
        this.filterRole = this.filterRole === role ? null : role;
    }

    toggleUnreadFilter(): void {
        this.filterUnread = !this.filterUnread;
    }

    loadNotes(): void {
        this._noteService.getCINotes(this.clearanceItemId).subscribe(notes => {
            this.notes = notes;
            this.captureUnreadSnapshot();
            this._cdr.detectChanges();
            this.scrollToBottom();
        });
    }

    private scrollToBottom(): void {
        // Defer so the DOM has rendered the (possibly newly added) notes before we scroll.
        setTimeout(() => {
            if (this.notesBody && this.notesBody.nativeElement) {
                this.notesBody.nativeElement.scrollTop = this.notesBody.nativeElement.scrollHeight;
            }
        }, 0);
    }

    addNote(): void {
        // Ignore if empty or a send is already in progress.
        if (this.isSending || !this.newNoteText.trim()) {
            return;
        }

        const note = new CINote();
        note.sfClearanceItemId = this.clearanceItemId;
        note.description = this.newNoteText.trim();

        this.isSending = true;
        this._cdr.detectChanges();

        this._noteService.insert(note).subscribe(
            saved => {
                this.notes.push(saved);
                this.newNoteText = '';
                this.isSending = false;
                this._cdr.detectChanges();
                this.scrollToBottom();
            },
            err => {
                // Unblock the input so the user can retry.
                console.error('Failed to send note:', err);
                this.isSending = false;
                this._cdr.detectChanges();
            }
        );
    }

    startEdit(note: CINote): void {
        this.editingNote = note;
        this.editText = note.description;
    }

    saveEdit(): void {
        if (!this.editText.trim()) {
            return;
        }
        this.editingNote.description = this.editText.trim();
        this._noteService.update(this.editingNote).subscribe(updated => {
            const idx = this.notes.findIndex(n => n.id === updated.id);
            if (idx !== -1) {
                this.notes[idx] = updated;
            }
            this.editingNote = null;
            this.editText = '';
            this._cdr.detectChanges();
        });
    }

    cancelEdit(): void {
        this.editingNote = null;
        this.editText = '';
    }

    removeNote(note: CINote): void {
        this._noteService.delete(note.id).subscribe(() => {
            this.notes = this.notes.filter(n => n.id !== note.id);
            if (this.editingNote && this.editingNote.id === note.id) {
                this.editingNote = null;
                this.editText = '';
            }
            this._cdr.detectChanges();
        });
    }

    canEdit(note: CINote): boolean {
        return note.createdById === this._authService.userInfo.userId;
    }

    isUnread(note: CINote): boolean {
        // Use the snapshot taken when the notes loaded / panel opened so that marking notes read on the
        // server (which happens as soon as the panel opens) doesn't instantly clear the unread indicator
        // for this session. Notes not in the snapshot (e.g. loaded fresh) fall back to the server flags.
        if (this.unreadSnapshot.has(note.id)) {
            return true;
        }
        return this.isServerUnread(note);
    }

    getViewedStatus(note: CINote): string {
        if (note.role === 'Production') {
            return note.isReadByClearance ? 'Viewed by Clearance' : 'Not yet viewed';
        }
        if (note.role === 'Clearance') {
            return note.isReadByProduction ? 'Viewed by Production' : 'Not yet viewed';
        }
        return '';
    }

    formatDate(date: Date): string {
        if (!date) {
            return '';
        }
        const d = new Date(date);
        const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
        const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
        return d.toLocaleDateString('en-US', options) + ' · ' + d.toLocaleTimeString('en-US', timeOptions);
    }
}
